import { useState, useEffect } from 'react'
import { X, ShieldCheck, ChevronDown, Loader2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useStackAbove } from '@/lib/useStackAbove'

const MINIMIZED_KEY = 'runna_impersonation_banner_minimized'

/**
 * Badge flutuante no canto inferior direito quando está impersonando.
 * Tem 2 estados:
 *   - minimizada (default): pill compacta com ícone + iniciais
 *   - expandida: card com detalhes (target, tenant, admin) + botão Encerrar
 */
export default function ImpersonationBanner() {
  const { impersonating, endImpersonation } = useAuth()
  const [minimized, setMinimized] = useState<boolean>(() => {
    return sessionStorage.getItem(MINIMIZED_KEY) !== '0'
  })
  const [ending, setEnding] = useState(false)
  const stackOffset = useStackAbove('[data-env-badge]')

  useEffect(() => {
    sessionStorage.setItem(MINIMIZED_KEY, minimized ? '1' : '0')
  }, [minimized])

  if (!impersonating) return null

  const { targetName, targetTenantName, adminName } = impersonating
  const targetInitials = targetName
    ? targetName.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()
    : '?'

  async function handleEnd() {
    setEnding(true)
    try { await endImpersonation() } finally { setEnding(false) }
  }

  if (minimized) {
    return (
      <button
        data-impersonation-banner
        onClick={() => setMinimized(false)}
        className="fixed right-4 z-[60] flex items-center gap-1.5 px-2.5 py-1.5 rounded-full shadow-lg cursor-pointer transition-all hover:brightness-110"
        style={{
          background: 'var(--color-primary)',
          color: 'white',
          bottom: `calc(1rem + var(--mobile-bottom-bar-pad, 0px) + ${stackOffset}px)`,
        }}
        title={`Impersonando ${targetName} — clique pra expandir`}
        aria-label="Expandir banner de impersonação"
      >
        <ShieldCheck className="w-3.5 h-3.5" />
        <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[9.5px] font-bold">
          {targetInitials}
        </span>
        <span className="text-[11px] font-semibold hidden sm:inline">Impersonando</span>
        <ChevronDown className="w-3 h-3 opacity-70 rotate-180" />
      </button>
    )
  }

  return (
    <div
      data-impersonation-banner
      className="fixed right-4 z-[60] rounded-xl shadow-xl p-3 max-w-[calc(100vw-2rem)] w-[320px]"
      style={{
        background: 'var(--color-primary)',
        color: 'white',
        bottom: 'calc(1rem + var(--mobile-bottom-bar-pad, 0px))',
      }}
    >
      <div className="flex items-start gap-2 mb-2">
        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0 text-[12px] leading-[1.35]">
          Impersonando <strong className="font-semibold">{targetName}</strong>
          <div className="opacity-80 text-[11px] mt-0.5">
            {targetTenantName} · admin: {adminName}
          </div>
        </div>
        <button
          onClick={() => setMinimized(true)}
          className="text-white/70 hover:text-white transition-colors shrink-0 cursor-pointer"
          title="Minimizar"
          aria-label="Minimizar"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>
      <button
        onClick={handleEnd}
        disabled={ending}
        className="w-full flex items-center justify-center gap-1.5 text-[12px] font-semibold px-3 py-2 rounded-md cursor-pointer transition-colors disabled:opacity-50"
        style={{ background: 'rgba(255,255,255,0.15)' }}
      >
        {ending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
        {ending ? 'Encerrando...' : 'Encerrar impersonação'}
      </button>
    </div>
  )
}
